import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import MediaUploadButton from '../../components/ui/MediaUploadButton';

const getMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  const baseUrl = import.meta.env.VITE_API_URL.endsWith('/api')
    ? import.meta.env.VITE_API_URL.slice(0, -4)
    : import.meta.env.VITE_API_URL;
  return `${baseUrl}${url.startsWith('/') ? '' : '/'}${url}`;
};

// ─── Status badge helper ──────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    sent: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    partial: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    failed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    no_subscribers: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
  };
  const labels = {
    sent: 'Sent',
    partial: 'Partial',
    failed: 'Failed',
    no_subscribers: 'No Subscribers'
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${map[status] || map.partial}`}>
      {labels[status] || status}
    </span>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const NotificationsPage = () => {
  const [selectedTab, setSelectedTab] = useState('create');
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    target: 'all',
    userId: '',
    icon: '',
    url: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  // User search state (server-side)
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const dropdownRef = useRef(null);

  // History pagination
  const [historyPage, setHistoryPage] = useState(1);

  const queryClient = useQueryClient();

  // ── Debounce the search input (300ms) ─────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(userSearchQuery), 300);
    return () => clearTimeout(timer);
  }, [userSearchQuery]);

  // ── Fetch subscribers count ────────────────────────────────────────────────
  const { data: subscribersData } = useQuery({
    queryKey: ['push-subscribers-count'],
    queryFn: async () => {
      const response = await api.get('/push/subscribers/count');
      return response.data.data;
    }
  });

  // ── Server-side user search ────────────────────────────────────────────────
  const { data: searchResults = [], isFetching: searchLoading } = useQuery({
    queryKey: ['push-user-search', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery.trim()) return [];
      const response = await api.get('/push/subscribers/search', {
        params: { q: debouncedQuery }
      });
      return response.data.data?.users || [];
    },
    enabled: !!debouncedQuery.trim(),
    staleTime: 30_000
  });

  // ── Notification history ───────────────────────────────────────────────────
  const {
    data: historyData,
    isLoading: historyLoading,
    isError: historyError
  } = useQuery({
    queryKey: ['push-history', historyPage],
    queryFn: async () => {
      const response = await api.get('/push/history', {
        params: { page: historyPage, limit: 15 }
      });
      return response.data.data;
    },
    enabled: selectedTab === 'history',
    keepPreviousData: true
  });

  // ── Close dropdown on outside click ───────────────────────────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Send push mutation ─────────────────────────────────────────────────────
  const sendPushMutation = useMutation({
    mutationFn: async (data) => {
      if (data.target === 'all') {
        return api.post('/push/send/all', data);
      }
      return api.post('/push/send/user', data);
    },
    onSuccess: (response) => {
      const { successCount, errorCount, hasSubscriptions } = response.data.data;

      if (successCount > 0) {
        toast.success(`Push notification sent to ${successCount} subscriber(s)`);
        setFormData({ title: '', message: '', target: 'all', userId: '', icon: '', url: '' });
        setSelectedUser(null);
        // Refresh history so the new entry appears immediately if on history tab
        queryClient.invalidateQueries({ queryKey: ['push-history'] });
      } else if (hasSubscriptions === false) {
        toast.error('This user has not enabled push notifications', { duration: 5000 });
        setSelectedUser(null);
        setFormData(prev => ({ ...prev, userId: '' }));
      } else if (errorCount > 0) {
        toast.error(`Failed to send to ${errorCount} subscriber(s)`);
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to send push notification');
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) {
      toast.error('Title and message are required');
      return;
    }
    if (formData.target === 'specific' && !formData.userId) {
      toast.error('Please select a user');
      return;
    }
    setIsLoading(true);
    try {
      await sendPushMutation.mutateAsync(formData);
    } catch {
      // Error already surfaced via onError
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleIconSelect = (media) => {
    const url = Array.isArray(media) ? media[0]?.url : media?.url;
    if (url) setFormData(prev => ({ ...prev, icon: url }));
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setFormData(prev => ({ ...prev, userId: user._id }));
    setUserDropdownOpen(false);
    setUserSearchQuery('');
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-admin-slate-900 dark:text-admin-slate-100">
            Push Notifications
          </h1>
          <p className="mt-1 text-sm text-admin-slate-600 dark:text-admin-slate-400">
            Create and manage push notifications for your users
          </p>
        </div>

        {subscribersData && (
          <div className="mt-4 sm:mt-0 flex items-center space-x-6">
            <div className="flex items-center space-x-2 bg-admin-slate-100 dark:bg-admin-slate-800 px-4 py-2 rounded-lg">
              <div className="p-2 bg-admin-ucla-100 dark:bg-admin-ucla-900/30 rounded-full">
                <svg className="w-5 h-5 text-admin-ucla-600 dark:text-admin-ucla-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-2.83-2h5.66A3 3 0 0110 18z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-admin-slate-500 dark:text-admin-slate-400">Unique Subscribers</p>
                <p className="text-lg font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                  {subscribersData.uniqueSubscribers}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 bg-admin-slate-100 dark:bg-admin-slate-800 px-4 py-2 rounded-lg">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-full">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-xs text-admin-slate-500 dark:text-admin-slate-400">Total Devices</p>
                <p className="text-lg font-semibold text-admin-slate-900 dark:text-admin-slate-100">
                  {subscribersData.activeSubscriptions}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-admin-slate-800 shadow-sm rounded-lg overflow-hidden">
        <div className="border-b border-admin-slate-200 dark:border-admin-slate-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {['create', 'history'].map((tab) => (
              <button
                key={tab}
                onClick={() => setSelectedTab(tab)}
                className={`${
                  selectedTab === tab
                    ? 'border-admin-ucla-500 text-admin-ucla-600 dark:text-admin-ucla-400'
                    : 'border-transparent text-admin-slate-500 dark:text-admin-slate-400 hover:text-admin-slate-700 dark:hover:text-admin-slate-300 hover:border-admin-slate-300 dark:hover:border-admin-slate-600'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 capitalize`}
              >
                {tab === 'create' ? 'Create Notification' : 'Notification History'}
              </button>
            ))}
          </nav>
        </div>

        {/* ── CREATE TAB ──────────────────────────────────────────────────── */}
        {selectedTab === 'create' && (
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6">

                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                    Notification Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="block w-full rounded-lg border-admin-slate-300 dark:border-admin-slate-600 bg-admin-slate-50 dark:bg-admin-slate-700 shadow-sm focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500 text-sm py-3 px-4 text-admin-slate-900 dark:text-admin-slate-100 placeholder-admin-slate-400 dark:placeholder-admin-slate-500 transition-colors duration-200"
                    placeholder="Enter notification title"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="block w-full rounded-lg border-admin-slate-300 dark:border-admin-slate-600 bg-admin-slate-50 dark:bg-admin-slate-700 shadow-sm focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500 text-sm py-3 px-4 text-admin-slate-900 dark:text-admin-slate-100 placeholder-admin-slate-400 dark:placeholder-admin-slate-500 transition-colors duration-200"
                    placeholder="Enter notification message"
                  />
                </div>

                {/* Target */}
                <div>
                  <label htmlFor="target" className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                    Target Audience *
                  </label>
                  <div className="relative">
                    <select
                      id="target"
                      name="target"
                      value={formData.target}
                      onChange={(e) => {
                        handleInputChange(e);
                        setSelectedUser(null);
                        setFormData(prev => ({ ...prev, userId: '', target: e.target.value }));
                      }}
                      className="block w-full rounded-lg border-admin-slate-300 dark:border-admin-slate-600 bg-admin-slate-50 dark:bg-admin-slate-700 shadow-sm focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500 text-sm py-3 pl-4 pr-10 text-admin-slate-900 dark:text-admin-slate-100 transition-colors duration-200 appearance-none cursor-pointer"
                      required
                    >
                      <option value="all">All Users</option>
                      <option value="specific">Specific User</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-admin-slate-500 dark:text-admin-slate-400">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Specific user search (server-side debounced) */}
                {formData.target === 'specific' && (
                  <div>
                    <label className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                      Select User *
                    </label>
                    <div className="relative" ref={dropdownRef}>
                      {/* Trigger / selected value */}
                      <div
                        onClick={() => setUserDropdownOpen(prev => !prev)}
                        className="block w-full rounded-lg border border-admin-slate-300 dark:border-admin-slate-600 bg-admin-slate-50 dark:bg-admin-slate-700 shadow-sm text-sm py-3 px-4 text-admin-slate-900 dark:text-admin-slate-100 transition-colors duration-200 cursor-pointer flex items-center justify-between"
                      >
                        {selectedUser ? (
                          <span>{selectedUser.name} <span className="text-admin-slate-400">({selectedUser.email})</span></span>
                        ) : (
                          <span className="text-admin-slate-400 dark:text-admin-slate-500">Type to search users...</span>
                        )}
                        <svg className="h-4 w-4 text-admin-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {/* Dropdown */}
                      {userDropdownOpen && (
                        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-admin-slate-800 border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg shadow-lg max-h-72 overflow-hidden flex flex-col">
                          {/* Search input */}
                          <div className="p-3 border-b border-admin-slate-200 dark:border-admin-slate-700">
                            <input
                              type="text"
                              value={userSearchQuery}
                              onChange={(e) => setUserSearchQuery(e.target.value)}
                              placeholder="Search by name or email..."
                              className="block w-full rounded-lg border-admin-slate-300 dark:border-admin-slate-600 bg-admin-slate-50 dark:bg-admin-slate-700 text-sm py-2 px-3 text-admin-slate-900 dark:text-admin-slate-100 placeholder-admin-slate-400 dark:placeholder-admin-slate-500 focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500"
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>

                          {/* Results list */}
                          <div className="overflow-y-auto flex-1">
                            {!debouncedQuery.trim() ? (
                              <div className="p-4 text-center text-sm text-admin-slate-400 dark:text-admin-slate-500">
                                Start typing to search users
                              </div>
                            ) : searchLoading ? (
                              <div className="p-4 text-center text-admin-slate-500 dark:text-admin-slate-400">
                                <svg className="animate-spin h-5 w-5 mx-auto" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                              </div>
                            ) : searchResults.length === 0 ? (
                              <div className="p-4 text-center text-sm text-admin-slate-500 dark:text-admin-slate-400">
                                No users found
                              </div>
                            ) : (
                              searchResults.map((user) => (
                                <div
                                  key={user._id}
                                  onClick={() => handleUserSelect(user)}
                                  className="px-4 py-3 hover:bg-admin-slate-100 dark:hover:bg-admin-slate-700 cursor-pointer transition-colors duration-200"
                                >
                                  <div className="font-medium text-sm text-admin-slate-900 dark:text-admin-slate-100">
                                    {user.name}
                                  </div>
                                  <div className="text-xs text-admin-slate-500 dark:text-admin-slate-400">
                                    {user.email}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {selectedUser && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedUser(null);
                          setFormData(prev => ({ ...prev, userId: '' }));
                        }}
                        className="mt-2 text-sm text-admin-ucla-600 dark:text-admin-ucla-400 hover:text-admin-ucla-700 dark:hover:text-admin-ucla-500"
                      >
                        Clear selection
                      </button>
                    )}
                  </div>
                )}

                {/* Icon + URL row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <span className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                      Icon (Optional)
                    </span>
                    <div className="space-y-3">
                      {formData.icon && (
                        <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-admin-slate-200 dark:border-admin-slate-600">
                          <img
                            src={getMediaUrl(formData.icon)}
                            alt="Notification icon preview"
                            className="w-full h-full object-contain bg-admin-slate-50 dark:bg-admin-slate-700/50"
                          />
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, icon: '' }))}
                            className="absolute top-0.5 right-0.5 p-0.5 bg-red-500 dark:bg-red-600 rounded-full text-white hover:bg-red-600 dark:hover:bg-red-700"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      )}
                      <MediaUploadButton onSelect={handleIconSelect} selectedMedia={formData.icon} />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="url" className="block text-sm font-medium text-admin-slate-700 dark:text-admin-slate-300 mb-2">
                      Action URL (Optional)
                    </label>
                    <input
                      type="url"
                      id="url"
                      name="url"
                      value={formData.url}
                      onChange={handleInputChange}
                      className="block w-full rounded-lg border-admin-slate-300 dark:border-admin-slate-600 bg-admin-slate-50 dark:bg-admin-slate-700 shadow-sm focus:ring-2 focus:ring-admin-ucla-500 focus:border-admin-ucla-500 text-sm py-3 px-4 text-admin-slate-900 dark:text-admin-slate-100 placeholder-admin-slate-400 dark:placeholder-admin-slate-500 transition-colors duration-200"
                      placeholder="/products or https://example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 pt-4 border-t border-admin-slate-200 dark:border-admin-slate-700">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-admin-ucla-600 hover:bg-admin-ucla-700 dark:bg-admin-ucla-700 dark:hover:bg-admin-ucla-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-admin-ucla-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      Send Push Notification
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ title: '', message: '', target: 'all', userId: '', icon: '', url: '' })}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-admin-slate-300 dark:border-admin-slate-600 shadow-sm text-sm font-medium rounded-lg text-admin-slate-700 dark:text-admin-slate-300 bg-white dark:bg-admin-slate-700 hover:bg-admin-slate-50 dark:hover:bg-admin-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-admin-ucla-500 transition-colors duration-200"
                >
                  Clear Form
                </button>
              </div>
            </form>
          </div>
        )}

        {/* ── HISTORY TAB ─────────────────────────────────────────────────── */}
        {selectedTab === 'history' && (
          <div className="p-6">
            {historyLoading ? (
              <div className="flex justify-center items-center py-16">
                <svg className="animate-spin h-8 w-8 text-admin-ucla-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : historyError ? (
              <div className="text-center py-12">
                <p className="text-sm text-red-600 dark:text-red-400">Failed to load notification history.</p>
              </div>
            ) : historyData?.logs?.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-admin-slate-400 dark:text-admin-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-admin-slate-900 dark:text-admin-slate-100">No notifications sent yet</h3>
                <p className="mt-1 text-sm text-admin-slate-500 dark:text-admin-slate-400">
                  Notifications you send will appear here.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => setSelectedTab('create')}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-admin-ucla-600 hover:bg-admin-ucla-700 dark:bg-admin-ucla-700 dark:hover:bg-admin-ucla-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-admin-ucla-500 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    Send First Notification
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {historyData.logs.map((log) => (
                    <div
                      key={log._id}
                      className="bg-white dark:bg-admin-slate-800 border border-admin-slate-200 dark:border-admin-slate-700 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 p-4"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold text-admin-slate-900 dark:text-admin-slate-100 truncate">
                              {log.title}
                            </h3>
                            <StatusBadge status={log.status} />
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              log.target === 'all'
                                ? 'bg-admin-ucla-100 dark:bg-admin-ucla-900/30 text-admin-ucla-700 dark:text-admin-ucla-300'
                                : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                            }`}>
                              {log.target === 'all' ? 'All Users' : `User: ${log.targetUserId?.name || 'Unknown'}`}
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-admin-slate-500 dark:text-admin-slate-400 line-clamp-2">
                            {log.message}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-admin-slate-500 dark:text-admin-slate-400">
                        {/* Delivery stats */}
                        <span className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          {log.successCount} delivered
                        </span>
                        {log.errorCount > 0 && (
                          <span className="flex items-center gap-1 text-red-500 dark:text-red-400">
                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            {log.errorCount} failed
                          </span>
                        )}
                        <span>of {log.totalTargeted} targeted</span>
                        <span>•</span>
                        <span>
                          Sent by {log.sentBy?.name || 'Admin'}
                        </span>
                        <span>•</span>
                        <span>
                          {new Date(log.createdAt).toLocaleString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {historyData.pagination && historyData.pagination.pages > 1 && (
                  <div className="mt-6 flex items-center justify-between border-t border-admin-slate-200 dark:border-admin-slate-700 pt-4">
                    <p className="text-sm text-admin-slate-500 dark:text-admin-slate-400">
                      Page {historyData.pagination.page} of {historyData.pagination.pages}
                      <span className="ml-2">({historyData.pagination.total} total)</span>
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                        disabled={historyData.pagination.page <= 1}
                        className="px-3 py-1.5 text-sm border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg text-admin-slate-700 dark:text-admin-slate-300 hover:bg-admin-slate-50 dark:hover:bg-admin-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setHistoryPage(p => p + 1)}
                        disabled={historyData.pagination.page >= historyData.pagination.pages}
                        className="px-3 py-1.5 text-sm border border-admin-slate-300 dark:border-admin-slate-600 rounded-lg text-admin-slate-700 dark:text-admin-slate-300 hover:bg-admin-slate-50 dark:hover:bg-admin-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;